import { FC } from "react";
import { Box, Grid } from "@mui/material";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
	CalculationMethods,
	ChainTransportModelType,
	EnvironmentalKpi,
	TransportModelType,
	TypeTransportModelType,
} from "../../../../../common/modelTypes";
import { colGrow } from "../../../../../common/utils";
import PieChartDistributionType, { DataChart } from "../../../../../components/charts/graph/PieChartDistributionType";
import NumericValueWidget from "../../../../../components/charts/NumericValueWidget";
import { AmbientalKpiWidget } from "../../../../../components/common/AmbientalKpiWidget";
import ContentLocked from "../../../../../components/common/ContentLocked";
import { MarketBaseType, MarketBaseWidget } from "../../../../../components/common/MarketBaseWidget";
import PermissionGridItem from "../../../../../components/common/PermissionGridItem";
import TableData from "../../../../../components/common/table/TableData";
import {
	DonutChartItemDTO,
	EmissionsTransportsResultsDatumDTO,
} from "../../../../../datasources/api-client/data-contracts";
import { getLockedContents } from "../../../../../permissions/permissionUtils";
import { useChartColors } from "../../../../../theme/theme";
import EmissionChaiTransport from "../../components/EmissionChainTransport";
import { dataColTransport, getColumnsTransport, getRowsTransport } from "./data";

interface Props {
	keyEmissions: string;
	transportEmissions: TransportModelType;
	calculationMethods: CalculationMethods;
	nodesCertification: CalculationMethods;
	environmentalKpi: EnvironmentalKpi;
	typeTransport: TypeTransportModelType;
	chainsTransport: ChainTransportModelType;
}

type EmissionType = keyof Pick<EmissionsTransportsResultsDatumDTO, "co2Ttw" | "co2Wtw">;

const ThirdParty: FC<Props> = ({
	transportEmissions,
	keyEmissions,
	calculationMethods,
	nodesCertification,
	environmentalKpi,
	typeTransport,
	chainsTransport,
}) => {
	const { t } = useTranslation("result.transport");
	const { chartColors } = useChartColors();

	// Color palette matching data order
	// The order is IMPORTANT
	const colors = [
		chartColors.method1,
		chartColors.method2,
		chartColors.method3,
		chartColors.method4,
		chartColors.method5,
		chartColors.method6,
	];

	const getDataChart = (data: DonutChartItemDTO[], t: TFunction): DataChart[] => {
		return (
			data.map((value, index) => {
				return {
					id: index,
					percent: value.percentage ? Number(value.percentage.toFixed(2)) : 0,
					value: value.value ?? 0,
					label: value.label || "",
					icon: <Box borderRadius={0.8} width={12} height={12} sx={{ backgroundColor: colors[index] }} />,
				};
			}) || []
		);
	};

	const overrideFieldName = (old?: string) => {
		switch (old) {
			case "emissions":
				return keyEmissions;
			default:
				return old;
		}
	};

	const isWtw = keyEmissions === "co2Wtw";

	const marketBase: MarketBaseType = {
		emissions: isWtw
			? transportEmissions.data?.thirdParty?.current.marketBasedWtw
			: transportEmissions.data?.thirdParty?.current.marketBasedTtw,
		delta: isWtw
			? transportEmissions.data?.thirdParty?.current.marketBasedWtwDelta
			: transportEmissions.data?.thirdParty?.current.marketBasedTtwDelta,
		coverage: isWtw
			? transportEmissions.data?.thirdParty?.current.marketBasedWtwCoverage
			: transportEmissions.data?.thirdParty?.current.marketBasedTtwCoverage,
		isLoading: transportEmissions.isLoading,
		onReload: transportEmissions.reload,
	};

	const titles = {
		transportEmissions: t("transport emissions"),
		grossWeight: t("gross weight"),
		tonsKm: t("tons km"),
		methodology: t("methodology used"),
		certification: t("certification presence"),
		emissionsByTransportMode: t("emissions by transport mode"),
		transportChain: t("transport chain results"),
		environmentalKpis: t("common:environmental kpis"),
		marketBased: t("common:market-based measures"),
	};

	const lockedContents = getLockedContents([
		{
			models: [transportEmissions],
			names: [
				titles.transportEmissions,
				titles.grossWeight,
				titles.tonsKm,
				titles.environmentalKpis,
				titles.marketBased,
			],
		},
		{ models: [calculationMethods], names: [titles.methodology] },
		{ models: [nodesCertification], names: [titles.certification] },
		{ models: [typeTransport], names: [titles.emissionsByTransportMode] },
		{ models: [chainsTransport], names: [titles.transportChain] },
	]);

	return (
		<Grid container spacing={2}>
			<Grid container size={12} spacing={2}>
				<PermissionGridItem models={[transportEmissions]} size={colGrow} height={180}>
					<NumericValueWidget
						title={titles.transportEmissions}
						delta={transportEmissions?.data?.thirdParty?.delta?.[keyEmissions as EmissionType]}
						value={transportEmissions.data?.thirdParty?.current?.[keyEmissions as EmissionType]?.value}
						measure={transportEmissions.data?.thirdParty?.current?.[keyEmissions as EmissionType]?.measure}
						isLoading={transportEmissions.isLoading}
						onReload={transportEmissions.reload}
					/>
				</PermissionGridItem>
				<PermissionGridItem models={[transportEmissions]} size={colGrow} height={180}>
					<NumericValueWidget
						title={titles.grossWeight}
						value={transportEmissions.data?.thirdParty.current?.weight.value}
						measure={transportEmissions.data?.thirdParty.current?.weight.measure}
						isLoading={transportEmissions.isLoading}
						onReload={transportEmissions.reload}
					/>
				</PermissionGridItem>
				<PermissionGridItem models={[transportEmissions]} size={colGrow} height={180}>
					<NumericValueWidget
						title={titles.tonsKm}
						value={transportEmissions.data?.thirdParty.current?.tkm.value}
						measure={transportEmissions.data?.thirdParty.current?.tkm.measure}
						isLoading={transportEmissions.isLoading}
						onReload={transportEmissions.reload}
					/>
				</PermissionGridItem>
			</Grid>

			<Grid container size={12} spacing={2}>
				<PermissionGridItem models={[calculationMethods]} size={colGrow} minHeight={320}>
					<PieChartDistributionType
						totalEmissions={calculationMethods?.data?.total.value}
						keyEmissions={calculationMethods?.data?.total.measure}
						title={titles.methodology}
						isLoading={calculationMethods?.isLoading || false}
						onReload={calculationMethods?.reload}
						dataChart={calculationMethods?.data?.items && getDataChart(calculationMethods?.data?.items, t)}
						colors={colors}
					/>
				</PermissionGridItem>
				<PermissionGridItem models={[nodesCertification]} size={colGrow} minHeight={320}>
					<PieChartDistributionType
						totalEmissions={nodesCertification?.data?.total.value}
						keyEmissions={nodesCertification?.data?.total.measure}
						title={titles.certification}
						isLoading={nodesCertification?.isLoading || false}
						onReload={nodesCertification?.reload}
						dataChart={nodesCertification?.data?.items && getDataChart(nodesCertification?.data?.items, t)}
						colors={colors}
					/>
				</PermissionGridItem>
			</Grid>

			<Grid container size={12} spacing={2}>
				<PermissionGridItem models={[typeTransport]} size={colGrow} minHeight={360}>
					<TableData
						columns={getColumnsTransport(
							dataColTransport,
							typeTransport.data ? typeTransport.data.total : {},
							t,
							overrideFieldName
						)}
						rows={typeTransport.data ? getRowsTransport(typeTransport.data, keyEmissions, t) : []}
						title={titles.emissionsByTransportMode}
						isLoading={typeTransport.isLoading}
						onReload={typeTransport.reload}
					/>
				</PermissionGridItem>
				<PermissionGridItem models={[chainsTransport]} size={colGrow}>
					<EmissionChaiTransport
						title={titles.transportChain}
						data={chainsTransport.data}
						isLoading={chainsTransport.isLoading}
						onReload={chainsTransport.reload}
					/>
				</PermissionGridItem>
			</Grid>

			<PermissionGridItem models={[transportEmissions]} size={12}>
				<AmbientalKpiWidget
					bio={transportEmissions.data?.thirdParty.current.biogenic}
					pmx={transportEmissions.data?.thirdParty.current.pmx}
					pmxCoverage={transportEmissions.data?.thirdParty.current.pmxCoverage}
					bioCoverage={transportEmissions.data?.thirdParty.current.biogenicCoverage}
					loading={transportEmissions.isLoading}
					onReload={transportEmissions.reload}
				/>
			</PermissionGridItem>

			<PermissionGridItem models={[transportEmissions]} size={12}>
				<MarketBaseWidget marketBase={marketBase} />
			</PermissionGridItem>

			{lockedContents.length > 0 && (
				<Grid size={12}>
					<ContentLocked items={lockedContents} />
				</Grid>
			)}
		</Grid>
	);
};

export default ThirdParty;
